import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HeadersService } from '../header.service';
import {
  CartaoElegivelOcorrencia,
  ConsultaOcorrenciasNovasParams,
  ConsultarRequisitosOcorrenciaNovaResponse,
  CriarOcorrenciaNovaRequest,
  CriarOcorrenciaNovaResponse,
  ListarCartoesElegiveisResponse,
  OcorrenciasNovasDetalhesResponse,
  OcorrenciasNovasFiltrosResponse,
  OcorrenciasPageResponse,
  OcorrenciasResponse
} from 'src/models/interfaces/ocorrencia/ocorrencia-interface';
import { LOADING_INSTANCE } from 'src/app/interceptor/network.interceptor';
import { SKIP_ERROR_INTERCEPTOR } from 'src/app/interceptor/error.interceptor';
import { OccurrenceLoadingKeys } from 'src/models/keys/occurrence/ocurrence.keys';

@Injectable({
  providedIn: 'root'
})
export class OccurrenceService {
  private readonly url = `${environment.apiUrl}/api/v1/ocorrencias`;
  private readonly urlOcorrenciasNovas = `${environment.apiUrl}/api/v1/ocorrenciasNovas`;

  /** Context do wizard: skeleton próprio + sem propagar mensagem ao MessageService da tela pai. */
  private readonly dialogHttpContext = new HttpContext()
    .set(LOADING_INSTANCE, OccurrenceLoadingKeys.NovaOcorrenciaDialog)
    .set(SKIP_ERROR_INTERCEPTOR, true);

  private readonly dialogCriarHttpContext = new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true);
  private readonly emptyOcorrenciasPage: OcorrenciasPageResponse = {
    content: [],
    pageable: {
      pageNumber: 0,
      pageSize: 25,
      sort: {
        empty: true,
        sorted: false,
        unsorted: true
      },
      offset: 0,
      paged: true,
      unpaged: false
    },
    last: true,
    totalPages: 0,
    totalElements: 0,
    size: 25,
    number: 0,
    sort: {
      empty: true,
      sorted: false,
      unsorted: true
    },
    first: true,
    numberOfElements: 0,
    empty: true
  };

  constructor(
    private readonly http: HttpClient,
    private headersService: HeadersService
  ) {
    this.headersService.includeObject({ fileName: 'occurrence.service.ts', url: this.url });
  }

  public getRetentionReasons(id: number): Observable<any> {
    return this.http
      .get<{ motivos: any }>(`${this.url}/personalizados/pedidos/${id}/motivos-de-retencao`)
      .pipe(map((response) => response.motivos));
  }

  public consultaOcorrenciasNovas(params: ConsultaOcorrenciasNovasParams = {}): Observable<OcorrenciasPageResponse> {
    let httpParams = new HttpParams();

    if (params.busca) httpParams = httpParams.set('busca', params.busca);
    if (params.idStatusOcorrencia != null) {
      httpParams = httpParams.set('idStatusOcorrencia', params.idStatusOcorrencia.toString());
    }
    if (params.idTipoOcorrencia != null) {
      httpParams = httpParams.set('idTipoOcorrencia', params.idTipoOcorrencia.toString());
    }
    if (params.idOrigemOcorrencia != null) {
      httpParams = httpParams.set('idOrigemOcorrencia', params.idOrigemOcorrencia.toString());
    }
    if (params.diasAbertura != null) {
      httpParams = httpParams.set('diasAbertura', params.diasAbertura.toString());
    }

    httpParams = httpParams.set('page', (params.page ?? 0).toString());
    httpParams = httpParams.set('size', (params.size ?? 25).toString());

    if (params.sort) {
      httpParams = httpParams.set('sort', params.sort);
    }

    return this.http
      .get<OcorrenciasResponse | null>(this.urlOcorrenciasNovas, {
        params: httpParams,
        context: new HttpContext().set(LOADING_INSTANCE, OccurrenceLoadingKeys.Default)
      })
      .pipe(map((response) => response?.ocorrencias ?? this.emptyOcorrenciasPage));
  }

  public getOcorrenciasNovasFiltros(): Observable<OcorrenciasNovasFiltrosResponse> {
    return this.http.get<OcorrenciasNovasFiltrosResponse>(`${this.urlOcorrenciasNovas}/filtros`, {
      context: new HttpContext().set(LOADING_INSTANCE, OccurrenceLoadingKeys.Default)
    });
  }

  public getOcorrenciasNovasDetalhes(
    idOcorrencia: number,
    idFuncionario: number
  ): Observable<OcorrenciasNovasDetalhesResponse> {
    return this.http.get<OcorrenciasNovasDetalhesResponse>(
      `${this.urlOcorrenciasNovas}/detalhes?idOcorrencia=${idOcorrencia}&idFuncionario=${idFuncionario}`,
      {
        context: new HttpContext().set(LOADING_INSTANCE, OccurrenceLoadingKeys.Details)
      }
    );
  }

  public getCartoesElegiveis(idFuncionario: number): Observable<ListarCartoesElegiveisResponse> {
    const params = new HttpParams().set('idFuncionario', idFuncionario.toString());

    return this.http.get<ListarCartoesElegiveisResponse>(`${this.urlOcorrenciasNovas}/cartoes-elegiveis`, {
      params,
      context: this.dialogHttpContext
    });
  }

  public getRequisitos(params: {
    idTipoOcorrencia: number;
    idFuncionario: number;
    idEmissor: number;
    idInsumo?: number;
    idCartao?: number;
  }): Observable<ConsultarRequisitosOcorrenciaNovaResponse> {
    let httpParams = new HttpParams()
      .set('idTipoOcorrencia', params.idTipoOcorrencia.toString())
      .set('idFuncionario', params.idFuncionario.toString())
      .set('idEmissor', params.idEmissor.toString());

    if (params.idInsumo != null) {
      httpParams = httpParams.set('idInsumo', params.idInsumo.toString());
    }

    if (params.idCartao != null) {
      httpParams = httpParams.set('idCartao', params.idCartao.toString());
    }

    return this.http.get<ConsultarRequisitosOcorrenciaNovaResponse>(`${this.urlOcorrenciasNovas}/requisitos`, {
      params: httpParams,
      context: this.dialogHttpContext
    });
  }

  public criarOcorrenciaNova(body: CriarOcorrenciaNovaRequest): Observable<CriarOcorrenciaNovaResponse> {
    // Sem LOADING_INSTANCE → NetworkInterceptor usa o loading global (trava a tela).
    // SKIP_ERROR_INTERCEPTOR → dialog trata mensagem; não dispara MessageService da tela pai.
    return this.http.post<CriarOcorrenciaNovaResponse>(`${this.urlOcorrenciasNovas}/criar`, body, {
      context: this.dialogCriarHttpContext
    });
  }
}
